import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import NotificationDropdown from 'src/@core/layouts/components/shared-components/NotificationDropdown'
import { formatNotificationTimestamp } from '@/src/@core/lib/formatNotificationTimestamp'
import type { Settings } from 'src/@core/context/settingsContext'
import { RootState } from '@/src/store'
import { NotificationCategory } from '@/src/store/apps/resto365Notifications'
import { useWebSocket } from '@/src/hooks/useWebSocket'
import { bhyveNavTokens as T } from './bhyveNavTokens'

/** Same drawer/menu as classic vertical nav + AppBar — not a route to submissions. */
const railNotificationSettings: Settings = {
  skin: 'default',
  mode: 'light',
  appBarBlur: false,
  direction: 'ltr',
  navCollapsed: true,
  themeColor: 'primary',
  contentWidth: 'full',
  verticalNavToggleType: 'accordion'
}

function getCategoryLabel(category: NotificationCategory): string {
  switch (category) {
    case 'menu':
      return 'Menu'
    case 'restaurant':
      return 'Restaurant'
    default:
      return ''
  }
}

export type BhyveNavNotificationsTriggerProps = {
  iconTapPx?: number
}

/**
 * Shared bell trigger for BHYBE rail footer + mobile bottom bar — same notifications payload as desktop rail.
 */
export const BhyveNavNotificationsTrigger: React.FC<BhyveNavNotificationsTriggerProps> = ({
  iconTapPx = T.footerUtilityIconTap
}) => {
  const {
    notifications: { value: notifications }
  } = useSelector((state: RootState) => state.resto365Notifications)
  const { isConnected } = useWebSocket()

  const notificationsForDropdown = useMemo(
    () =>
      notifications.map(notification => {
        const { message, category, createdAt, read, clientKey } = notification

        return {
          meta: getCategoryLabel(category),
          title: message,
          subtitle: formatNotificationTimestamp(createdAt),
          read,
          clientKey: clientKey ?? ''
        }
      }),
    [notifications]
  )

  return (
    <NotificationDropdown
      settings={railNotificationSettings}
      isSocketConnected={isConnected}
      sidebar
      sidebarTrigger={{
        iconOnly: true,
        bellIcon: 'mdi:bell-outline',
        bellIconColor: T.sidebarText,
        iconButtonSx: {
          width: iconTapPx,
          height: iconTapPx,
          color: T.sidebarText,
          borderRadius: 1,
          '&:hover': { bgcolor: T.sidebarRowHover }
        }
      }}
      notifications={notificationsForDropdown}
    />
  )
}
